import { pgTable, text, serial, integer, boolean, date } from "drizzle-orm/pg-core";
import { sql } from "drizzle-orm";
import { createInsertSchema } from "drizzle-zod";
import { z } from "zod";

// Player model
export const players = pgTable("players", {
  id: serial("id").primaryKey(),
  username: text("username").notNull().unique(),
  wins: integer("wins").notNull().default(0),
  losses: integer("losses").notNull().default(0),
  draws: integer("draws").notNull().default(0),
});

// User wallet stats table
export const userWalletStats = pgTable("user_wallet_stats", {
  id: serial("id").primaryKey(),
  walletAddress: text("wallet_address").notNull().unique(),
  wins: integer("wins").notNull().default(0),
  draws: integer("draws").notNull().default(0),
  losses: integer("losses").notNull().default(0),
  dailyGamesCount: integer("daily_games_count").default(0),
  lastGameDate: date("last_game_date").default(sql`CURRENT_DATE`),
});

// Discord usernames table
export const discordUsernames = pgTable("discord_usernames", {
  id: serial("id").primaryKey(),
  walletAddress: text("wallet_address").notNull().unique(),
  discordUsername: text("discord_username").notNull().unique(),
  createdAt: text("created_at").default(sql`CURRENT_TIMESTAMP`),
  updatedAt: text("updated_at").default(sql`CURRENT_TIMESTAMP`),
});

// Game voting table
export const gameVotes = pgTable("game_votes", {
  id: serial("id").primaryKey(),
  walletAddress: text("wallet_address").notNull().unique(),
  gameChoice: text("game_choice").notNull(), // Chess, Poker, or Geoguessr
  createdAt: text("created_at").notNull(),
});

// Decrypted Throne entries table
export const decryptedThroneEntries = pgTable("decrypted_throne_entries", {
  id: serial("id").primaryKey(),
  discordName: text("discord_name").notNull(),
  walletAddress: text("wallet_address").notNull().unique(),
  votingOption: integer("voting_option").notNull(),
  createdAt: text("created_at").notNull(),
  arciumTxId: text("arcium_tx_id"),
  lotteryTicket: text("lottery_ticket"),
});

// Tweet contest entries table
export const tweetContestEntries = pgTable("tweet_contest_entries", {
  id: serial("id").primaryKey(),
  walletAddress: text("wallet_address").notNull(),
  tweetUrl: text("tweet_url").notNull(),
  createdAt: text("created_at").notNull(),
});

// Game lobbies table
export const gameLobbies = pgTable("game_lobbies", {
  id: serial("id").primaryKey(),
  walletAddress: text("wallet_address").notNull(),
  joinerWallet: text("joiner_wallet"),
  playerCount: integer("player_count").notNull().default(1),
  creatorChoice: integer("creator_choice"),
  joinerChoice: integer("joiner_choice"),
  creatorTxId: text("creator_tx_id"),
  joinerTxId: text("joiner_tx_id"),
  createdAt: text("created_at").notNull(),
  status: text("status").notNull().default("waiting"), // waiting, in_progress, completed
});

// Game session model
export const gameSessions = pgTable("game_sessions", {
  id: serial("id").primaryKey(),
  player1Id: integer("player1_id").notNull().references(() => players.id),
  player2Id: integer("player2_id").references(() => players.id),
  player1Move: text("player1_move"),
  player2Move: text("player2_move"),
  result: text("result"),
  isComplete: boolean("is_complete").notNull().default(false),
  createdAt: text("created_at").notNull(),
});

// Insert schemas
export const insertPlayerSchema = createInsertSchema(players as any).omit({
  id: true,
  wins: true,
  losses: true,
  draws: true,
});

export const insertUserWalletStatsSchema = createInsertSchema(userWalletStats as any).omit({
  id: true,
});

export const insertDiscordUsernameSchema = createInsertSchema(discordUsernames as any).omit({
  id: true,
});

export const insertGameSessionSchema = createInsertSchema(gameSessions as any).omit({
  id: true,
});

export const insertGameVoteSchema = createInsertSchema(gameVotes as any).omit({
  id: true,
});

export const insertDecryptedThroneEntrySchema = createInsertSchema(decryptedThroneEntries as any).omit({
  id: true,
});

export const insertTweetContestEntrySchema = createInsertSchema(tweetContestEntries as any).omit({
  id: true,
});

export const insertGameLobbySchema = createInsertSchema(gameLobbies as any).omit({
  id: true,
});

// Types
export type InsertPlayer = z.infer<typeof insertPlayerSchema>;
export type Player = typeof players.$inferSelect;

export type InsertUserWalletStats = z.infer<typeof insertUserWalletStatsSchema>;
export type UserWalletStats = typeof userWalletStats.$inferSelect;

export type InsertDiscordUsername = z.infer<typeof insertDiscordUsernameSchema>;
export type DiscordUsername = typeof discordUsernames.$inferSelect;

export type InsertGameSession = z.infer<typeof insertGameSessionSchema>;
export type GameSession = typeof gameSessions.$inferSelect;

export type InsertGameVote = z.infer<typeof insertGameVoteSchema>;
export type GameVote = typeof gameVotes.$inferSelect;

export type InsertDecryptedThroneEntry = z.infer<typeof insertDecryptedThroneEntrySchema>;
export type DecryptedThroneEntry = typeof decryptedThroneEntries.$inferSelect;

export type InsertTweetContestEntry = z.infer<typeof insertTweetContestEntrySchema>;
export type TweetContestEntry = typeof tweetContestEntries.$inferSelect;

export type InsertGameLobby = z.infer<typeof insertGameLobbySchema>;
export type GameLobby = typeof gameLobbies.$inferSelect;

// Game move type
export enum Move {
  ROCK = "rock",
  PAPER = "paper",
  SCISSORS = "scissors",
}

// Game result type
export enum GameResult {
  PLAYER1_WIN = "player1_win",
  PLAYER2_WIN = "player2_win",
  DRAW = "draw",
}

// Game choice options
export enum GameChoice {
  CHESS = "Chess",
  POKER = "Poker",
  GEOGUESSR = "Geoguessr",
}
